import {
  Component,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  Input,
  Inject,
} from '@angular/core';
import { LayoutService } from '../../services/layout.service';
import { LayoutConfig, MenuItem } from '../../models';
import { LAYOUT_CONFIG_TOKEN } from '../../services/layout.token';
import { DialogService } from '@seech/ux-ng';

interface User {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email?: string;
  profileImageURL?: string;
  profileBannerURL?: string;
}

@Component({
  standalone: false,
  selector: 'sub-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnChanges {
  @Input() logoUrl = ``;
  @Input() logoLink = '/home'; // Configurable link for the logo
  @Input() searchPlaceholder = 'Search'; // Configurable search placeholder
  @Input() menuItems: MenuItem[] = []; // Dynamic menu items
  @Input() user = {} as User; // User data passed from parent component

  /**
   * Optional workspace line under the identity block — the Merchant a Merchant
   * user is acting for, the tenant an Operations user is signed in to. Sites
   * with no such context leave it unset and the line is not rendered.
   */
  @Input() userContext = '';

  @Output() openMenu: EventEmitter<void> = new EventEmitter<void>(); // Emit event for opening the menu
  showSearch!: boolean;

  /**
   * `menuItems` split on `group`, computed on input rather than in a getter so
   * the template's `@for` tracks stable array identities instead of a new set
   * of arrays on every change-detection pass.
   */
  menuGroups: MenuItem[][] = [];

  constructor(
    private layoutService: LayoutService,
    @Inject(LAYOUT_CONFIG_TOKEN) private config: LayoutConfig,
    private dialogService: DialogService,
  ) { }

  ngOnInit(): void {
    this.showSearch = this.layoutService.config?.hasSearch ?? false;
    this.logoUrl = `${this.config.globalCdnUrl}/images/seech-logo.png`;
    // Also here, not only in ngOnChanges: a host that sets `menuItems` on the
    // instance rather than through a binding never triggers a change hook.
    this.menuGroups = this.groupMenuItems(this.menuItems);
  }

  ngOnChanges(): void {
    this.menuGroups = this.groupMenuItems(this.menuItems);
  }

  /** Name for the identity block, falling back to the username. */
  get displayName(): string {
    const name = `${this.user?.firstName ?? ''} ${this.user?.lastName ?? ''}`.trim();
    return name || this.user?.username || '';
  }

  /**
   * The second line of the identity block.
   *
   * The bar carries the username and the panel carries the email, so the two
   * together say who you are and where you are reachable. Falling back to the
   * username here would only repeat the bar a few pixels below itself, so a site
   * with no email on its session shows the name alone instead.
   */
  get identitySubline(): string {
    return this.user?.email?.trim() ?? '';
  }

  onMenuItemClick(item: MenuItem): void
  {
    if (item.external && item.routerLink) {
        window.location.href = item.routerLink;
    } else if (item.routerLink) {
       if (item.routerLink.startsWith('/logout')) {
        this.logout(item);
      } else {
        window.location.href = item.routerLink; // Fallback to full page reload for internal links
      }
    }
  }

  /**
   * Confirm before ending the session.
   *
   * The slots read backwards on purpose. `seech-dialog` renders `actionBtnText`
   * first and `cancelBtnText` second, so the only way to put the confirming
   * action on the right — where this app puts it everywhere else — is to hand
   * Cancel to the action slot and Log out to the cancel slot. The callbacks
   * follow the buttons, not the slot names.
   *
   * `modalClass` carries a class of our own so the styling below reaches this
   * dialog and not the ones account.seech and payment.seech open through the
   * same service.
   */
  logout(item: MenuItem) {
    const title = 'Log out?';
    const content =
      '<p class="sub-logout__text">' +
      'You will be signed out on this device. Anything you have saved stays where it is.' +
      '</p>';
    const buttonConfig = {
      actionBtnText: 'Cancel',
      cancelBtnText: 'Log out',
      actionBtnClasses: 'sub-logout__btn sub-logout__btn--quiet',
      cancelBtnClasses: 'sub-logout__btn sub-logout__btn--danger',
      onAction: () => {
        this.dialogService.close();
      },
      onCancel: () => {
        window.location.href = item.routerLink!;
      },
      modalClass: 'modal-dialog modal-dialog-centered sub-logout',
    };

    this.dialogService.open(title, content, buttonConfig);
  }

  /**
   * Consecutive items sharing a `group` land in the same bucket. Grouping is
   * positional so a site orders the menu by ordering the array; an item with no
   * group joins whatever bucket precedes it rather than opening a new one.
   */
  private groupMenuItems(items: MenuItem[]): MenuItem[][] {
    const groups: MenuItem[][] = [];
    let currentKey: string | undefined;

    for (const item of items ?? []) {
      if (item.isHidden) continue;

      const key = item.group;
      if (!groups.length || (key !== undefined && key !== currentKey)) {
        groups.push([]);
        currentKey = key;
      }
      groups[groups.length - 1].push(item);
    }

    return groups.filter((group) => group.length > 0);
  }
}
